import { useEffect, useMemo, useRef } from "react";
import { useGetIntentTopicsByIdsLazyQuery } from "@/generated/taxonomy";
import { TAXONOMY_CLIENT } from "@/utils/consts";
import { useCompanyPageStore } from "@/store/companyPageStore";
import type { CachedTopic } from "@/types/intent";

function mergeTopicsById(
  existing: CachedTopic[],
  incoming: CachedTopic[]
): CachedTopic[] {
  if (incoming.length === 0) return existing;

  const existingIds = new Set(existing.map((t) => t.id));
  const newTopics = incoming.filter((t) => !existingIds.has(t.id));
  if (newTopics.length === 0) return existing;

  return [...existing, ...newTopics];
}

/**
 * Hydrates topic display names into `intentFilters.topicsCache` when `topicIds`
 * are pre-populated (e.g. opening CompanyDrawer from Segments Explore).
 */
export function useCompanyIntentTopicsHydration(): void {
  const topicIds = useCompanyPageStore((s) => s.intentFilters.topicIds);
  const topicsCache = useCompanyPageStore((s) => s.intentFilters.topicsCache);
  const setIntentFilters = useCompanyPageStore((s) => s.setIntentFilters);

  const [fetchTopicsByIds] = useGetIntentTopicsByIdsLazyQuery({
    context: { clientName: TAXONOMY_CLIENT },
  });

  const cachedIds = useMemo(
    () => new Set(topicsCache.map((t) => t.id)),
    [topicsCache]
  );

  const requestRef = useRef<string | null>(null);
  const attemptedIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (topicIds.length === 0) return;

    const idsToFetch = topicIds.filter(
      (id) => !cachedIds.has(id) && !attemptedIdsRef.current.has(id)
    );
    if (idsToFetch.length === 0) return;

    const requestKey = idsToFetch.join(",");
    if (requestRef.current === requestKey) return;
    requestRef.current = requestKey;

    fetchTopicsByIds({
      variables: { ids: idsToFetch },
      context: { clientName: TAXONOMY_CLIENT },
    })
      .then(({ data }) => {
        if (requestRef.current !== requestKey) return;

        const topicsFromApi =
          data?.intentTopic?.data?.filter(
            (topic): topic is NonNullable<typeof topic> =>
              topic !== null && topic.id !== null && topic.displayName !== null
          ) ?? [];

        if (topicsFromApi.length === 0) return;

        const returnedIds = new Set<string>(
          topicsFromApi.map((t) => t.id as string)
        );
        idsToFetch
          .filter((id) => !returnedIds.has(id))
          .forEach((id) => attemptedIdsRef.current.add(id));

        const hydratedTopics: CachedTopic[] = topicsFromApi.map((topic) => ({
          id: topic.id as string,
          displayName: topic.displayName as string,
          category: topic.category ?? undefined,
          collectionTree: (topic.collectionTree ?? []).filter(
            (t): t is string => t !== null
          ),
        }));

        setIntentFilters((prev) => {
          const nextCache = mergeTopicsById(prev.topicsCache, hydratedTopics);
          if (nextCache === prev.topicsCache) return prev;
          return { ...prev, topicsCache: nextCache };
        });
      })
      .catch(() => {
        // Silent: hydration can retry on later renders
      })
      .finally(() => {
        if (requestRef.current === requestKey) {
          requestRef.current = null;
        }
      });
  }, [cachedIds, fetchTopicsByIds, setIntentFilters, topicIds]);
}
